import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '@/context/CartContext';
import { Button } from '@/components/ui/button';
import { Trash2, Plus, Minus } from 'lucide-react';
import { toast } from 'sonner';

export const CartPage = () => {
  const { cart, cartTotal, updateCart, removeFromCart } = useCart();
  const navigate = useNavigate();

  const handleUpdateQuantity = async (itemId, newQuantity) => {
    try {
      await updateCart(itemId, newQuantity);
    } catch (error) {
      toast.error('Failed to update cart');
    }
  };

  const handleRemove = async (itemId) => {
    try {
      await removeFromCart(itemId);
    } catch (error) {
      toast.error('Failed to remove item');
    }
  };

  if (cart.length === 0) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center" data-testid="empty-cart">
        <h2 className="font-outfit font-semibold text-2xl mb-4">Your cart is empty</h2>
        <p className="text-muted-foreground mb-6">Add some items to get started!</p>
        <Link to="/products">
          <Button className="rounded-full px-8 py-6 bg-primary text-primary-foreground hover:bg-primary/90 font-bold" data-testid="shop-now-button">
            Shop Now
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8" data-testid="cart-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="font-outfit font-semibold text-3xl md:text-4xl mb-8" data-testid="cart-heading">
          Shopping Cart ({cart.length})
        </h1>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4" data-testid="cart-items-list">
            {cart.map((item) => {
              const price = item.product.price;
              return (
                <div
                  key={item.id}
                  className="flex gap-4 p-4 bg-white rounded-lg border border-border"
                  data-testid={`cart-item-${item.id}`}
                >
                  <Link to={`/product/${item.product.id}`} className="flex-shrink-0">
                    <img
                      src={item.product.images[0]}
                      alt={item.product.name}
                      className="w-24 h-32 object-cover rounded-lg"
                    />
                  </Link>

                  <div className="flex-1 space-y-2">
                    <Link to={`/product/${item.product.id}`}>
                      <h3 className="font-medium hover:text-primary" data-testid={`cart-item-name-${item.id}`}>
                        {item.product.name}
                      </h3>
                    </Link>
                    <p className="text-sm text-muted-foreground">
                      Size: {item.size} | Color: {item.color}
                    </p>
                    <p className="font-mono font-bold text-lg">₹ {price.toFixed(2)}</p>

                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2 border border-border rounded-full">
                        <button
                          onClick={() => handleUpdateQuantity(item.id, item.quantity - 1)}
                          className="p-2 hover:bg-secondary rounded-full"
                          data-testid={`decrease-quantity-${item.id}`}
                        >
                          <Minus className="w-4 h-4" />
                        </button>
                        <span className="w-8 text-center" data-testid={`quantity-${item.id}`}>{item.quantity}</span>
                        <button
                          onClick={() => handleUpdateQuantity(item.id, item.quantity + 1)}
                          className="p-2 hover:bg-secondary rounded-full"
                          data-testid={`increase-quantity-${item.id}`}
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>

                      <button
                        onClick={() => handleRemove(item.id)}
                        className="text-destructive hover:text-destructive/80"
                        data-testid={`remove-item-${item.id}`}
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 bg-white rounded-lg border border-border p-6 space-y-4" data-testid="order-summary">
              <h2 className="font-semibold text-xl">Order Summary</h2>

              <div className="space-y-2 py-4 border-y border-border">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span className="font-medium">₹ {cartTotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Shipping</span>
                  <span className="font-medium">Free</span>
                </div>
              </div>

              <div className="flex justify-between text-lg font-bold">
                <span>Total</span>
                <span data-testid="cart-total">₹ {cartTotal.toFixed(2)}</span>
              </div>

              <Button
                onClick={() => navigate('/checkout')}
                className="w-full rounded-full py-6 bg-primary text-primary-foreground hover:bg-primary/90 font-bold"
                data-testid="proceed-to-checkout-button"
              >
                Proceed to Checkout
              </Button>

              <Link to="/products" className="block text-center text-sm text-muted-foreground hover:text-primary">
                Continue Shopping
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
